import { useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"

import { normalizeHubspotId } from "@/lib/kanban-data"
import type { KanbanFeature } from "@/types/kanban"
import {
  getCandidateStage,
  getHubspotStageLabel,
  normalizeStageDesiredLabel,
  type CandidateStatusId,
  type StageAssociationLookup,
} from "@/types/candidate"

import {
  useSetApplicationStatus,
  type SetApplicationStatusHistoryContext,
} from "@/hooks/supabase/useSetApplicationStatus"

type ResolveIdentifiersArgs = {
  feature: KanbanFeature
  previousFeature?: KanbanFeature | null
  fallbackVacancyId?: string | null
}

type ResolvedIdentifiers = {
  vacancyId: string | null
  contactId: string | null
  companyId: string | null
}

type CandidateStatusUpdateParams = {
  feature: KanbanFeature
  nextStatus: CandidateStatusId
  previousFeature?: KanbanFeature | null
  fallbackVacancyId?: string | null
  stageAssociations?: StageAssociationLookup
  historyContext?: CandidateStatusHistoryContext
}

export type CandidateStatusUpdateOutcome =
  | {
      status: "fulfilled"
      feature: KanbanFeature
      appliedLabel: string
      associationTypeId: string | null
    }
  | {
      status: "skipped"
      feature: KanbanFeature
      reason: Error
    }
  | {
      status: "rejected"
      feature: KanbanFeature
      reason: unknown
    }

const resolveIdentifiers = ({
  feature,
  previousFeature,
  fallbackVacancyId,
}: ResolveIdentifiersArgs): ResolvedIdentifiers => {
  const vacancyCandidates = [
    feature.vacancyId,
    previousFeature?.vacancyId,
    fallbackVacancyId,
  ]

  const contactCandidates = [feature.contactId, previousFeature?.contactId]

  const vacancyId =
    vacancyCandidates
      .map((value) => normalizeHubspotId(value))
      .find((value): value is string => Boolean(value)) ?? null

  const contactId =
    contactCandidates
      .map((value) => normalizeHubspotId(value))
      .find((value): value is string => Boolean(value)) ?? null

  const companyId = feature.owner?.id ?? previousFeature?.owner?.id ?? null

  return {
    vacancyId,
    contactId,
    companyId,
  }
}

export type CandidateStatusHistoryContext = {
  vacancyName?: string | null
  vacancyTitle?: string | null
  vacancyLocation?: string | null
  vacancyDiscipline?: string | null
  companyName?: string | null
}

export const useCandidateStatusUpdater = () => {
  const { setStatus } = useSetApplicationStatus()
  const { user } = useAuth0()
  const fallbackActorEmail = "system@unknown.local"
  const fallbackActorName = "System"
  const actorEmail = user?.email ?? fallbackActorEmail
  const actorName = user?.nickname ?? user?.name ?? fallbackActorName

  const syncStatus = useCallback(
    async ({
      feature,
      nextStatus,
      previousFeature,
      fallbackVacancyId,
      stageAssociations,
      historyContext,
    }: CandidateStatusUpdateParams): Promise<CandidateStatusUpdateOutcome> => {
      const identifiers = resolveIdentifiers({
        feature,
        previousFeature,
        fallbackVacancyId,
      })

      if (!identifiers.vacancyId || !identifiers.contactId) {
        const reason = new Error("Missing vacancyId or contactId for status sync")
        console.warn("[candidate-status] skipping sync due to missing identifiers", {
          featureId: feature.id,
          vacancyId: identifiers.vacancyId,
          contactId: identifiers.contactId,
        })
        return {
          status: "skipped",
          feature,
          reason,
        }
      }

      const stage = getCandidateStage(nextStatus)
      if (!stage) {
        const reason = new Error(`Unknown candidate stage id: ${nextStatus}`)
        console.warn("[candidate-status] skipping sync due to unknown stage", {
          featureId: feature.id,
          stageId: nextStatus,
        })
        return {
          status: "skipped",
          feature,
          reason,
        }
      }

      const association = stageAssociations?.[nextStatus]
      const labelCandidate =
        association?.label ??
        feature.statusLabel ??
        getHubspotStageLabel(nextStatus) ??
        stage.label
      const desiredLabel =
        normalizeStageDesiredLabel(nextStatus, labelCandidate, association?.slug) ??
        stage.label
      const associationTypeId =
        association?.typeId ?? feature.associationTypeId ?? previousFeature?.associationTypeId ?? null

      const normalizeText = (value?: string | null) => {
        if (typeof value !== "string") {
          return null
        }
        const trimmed = value.trim()
        return trimmed.length > 0 ? trimmed : null
      }

      const buildHistoryContextPayload = (): SetApplicationStatusHistoryContext | undefined => {
        const candidateFullName =
          normalizeText(feature.name) ??
          normalizeText([feature.firstName, feature.lastName].filter(Boolean).join(" ")) ??
          normalizeText(feature.email ?? null)
        const candidateEmail = normalizeText(feature.email ?? null)
        const candidatePhone = normalizeText(feature.phone ?? null)
        const vacancyName = normalizeText(historyContext?.vacancyName ?? null)
        const vacancyTitle = normalizeText(historyContext?.vacancyTitle ?? null)
        const vacancyLocation = normalizeText(historyContext?.vacancyLocation ?? null)
        const vacancyDiscipline = normalizeText(historyContext?.vacancyDiscipline ?? null)
        const companyName = normalizeText(
          historyContext?.companyName ?? feature.owner?.name ?? null
        )

        const payload: SetApplicationStatusHistoryContext = {}
        if (candidateFullName) payload.candidateName = candidateFullName
        if (candidateEmail) payload.candidateEmail = candidateEmail
        if (candidatePhone) payload.candidatePhone = candidatePhone
        if (vacancyName) payload.vacancyName = vacancyName
        if (vacancyTitle) payload.vacancyTitle = vacancyTitle
        if (vacancyLocation) payload.vacancyLocation = vacancyLocation
        if (vacancyDiscipline) payload.vacancyDiscipline = vacancyDiscipline
        if (companyName) payload.companyName = companyName

        return Object.keys(payload).length > 0 ? payload : undefined
      }

      const historyPayload = buildHistoryContextPayload()

      try {
        await setStatus({
          vacancyId: identifiers.vacancyId,
          contactId: identifiers.contactId,
          desiredLabel,
          associationTypeId: associationTypeId ?? undefined,
          stageId: nextStatus,
          companyId: identifiers.companyId,
          actorEmail,
          actorName,
          source: "ui",
          historyContext: historyPayload,
        })

        return {
          status: "fulfilled",
          feature,
          appliedLabel: desiredLabel,
          associationTypeId,
        }
      } catch (error) {
        console.error("[candidate-status] failed to sync", {
          featureId: feature.id,
          stageId: nextStatus,
          error,
        })
        return {
          status: "rejected",
          feature,
          reason: error,
        }
      }
    },
    [setStatus, actorEmail, actorName]
  )

  const syncStatusBatch = useCallback(
    async (
      updates: CandidateStatusUpdateParams[]
    ): Promise<CandidateStatusUpdateOutcome[]> => {
      return Promise.all(updates.map((item) => syncStatus(item)))
    },
    [syncStatus]
  )

  return {
    syncStatus,
    syncStatusBatch,
  }
}
